import { useCallback, useRef, useState } from 'react';
import type { PrintPlacement } from '@wrapforce/shared';

const HISTORY_CAP = 100;

// Undo/redo over the whole placements array. A drag gesture (pointer down ->
// move -> up) must produce exactly one history entry: callers snapshot with
// beginGesture() at pointerdown, stream live updates with updateLive() during
// the drag (no history push), then push the pre-gesture snapshot with
// commitGesture() at pointerup. Discrete actions (toolbar buttons, numeric
// inputs) skip the gesture dance and call commit() directly.
// `onCommit` fires with the exact array that becomes the new present,
// exactly once per committed change (never during a live drag) - the caller
// uses it to forward the change to the parent's onChange prop.
export function usePlacementHistory(initial: PrintPlacement[], onCommit?: (next: PrintPlacement[]) => void) {
  const [present, setPresent] = useState<PrintPlacement[]>(initial);
  const pastRef = useRef<PrintPlacement[][]>([]);
  const futureRef = useRef<PrintPlacement[][]>([]);
  const gestureSnapshotRef = useRef<PrintPlacement[] | null>(null);

  const reset = useCallback((next: PrintPlacement[]) => {
    pastRef.current = [];
    futureRef.current = [];
    gestureSnapshotRef.current = null;
    setPresent(next);
  }, []);

  // Discrete committed change (not part of a drag gesture): pushes the
  // current present onto past, then sets present = next.
  const commit = useCallback((next: PrintPlacement[]) => {
    pastRef.current = [...pastRef.current, present].slice(-HISTORY_CAP);
    futureRef.current = [];
    setPresent(next);
    onCommit?.(next);
  }, [present, onCommit]);

  const beginGesture = useCallback(() => {
    gestureSnapshotRef.current = present;
  }, [present]);

  // Live update during a drag: replaces present without touching history.
  const updateLive = useCallback((next: PrintPlacement[]) => {
    setPresent(next);
  }, []);

  const commitGesture = useCallback((next: PrintPlacement[]) => {
    const snapshot = gestureSnapshotRef.current;
    gestureSnapshotRef.current = null;
    if (snapshot) {
      pastRef.current = [...pastRef.current, snapshot].slice(-HISTORY_CAP);
      futureRef.current = [];
    }
    setPresent(next);
    onCommit?.(next);
  }, [onCommit]);

  const cancelGesture = useCallback(() => {
    const snapshot = gestureSnapshotRef.current;
    gestureSnapshotRef.current = null;
    if (snapshot) setPresent(snapshot);
  }, []);

  const undo = useCallback(() => {
    const past = pastRef.current;
    if (past.length === 0) return;
    const previous = past[past.length - 1]!;
    pastRef.current = past.slice(0, -1);
    futureRef.current = [present, ...futureRef.current].slice(0, HISTORY_CAP);
    setPresent(previous);
    onCommit?.(previous);
  }, [present, onCommit]);

  const redo = useCallback(() => {
    const future = futureRef.current;
    if (future.length === 0) return;
    const next = future[0]!;
    futureRef.current = future.slice(1);
    pastRef.current = [...pastRef.current, present].slice(-HISTORY_CAP);
    setPresent(next);
    onCommit?.(next);
  }, [present, onCommit]);

  return {
    present,
    reset,
    commit,
    beginGesture,
    updateLive,
    commitGesture,
    cancelGesture,
    undo,
    redo,
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
  };
}
